import type { Filter, PropertyType } from 'contexts/preferences/listing-params/types';

/** Boolean amenity filter keys a summary can surface, in display order. */
export type SearchAmenityKey = 'openHouse' | 'garage' | 'pool' | 'fireplace' | 'waterfront';

export interface NumericRange {
  min: number | null;
  max: number | null;
}

/**
 * The meaningful, non-default facets of a saved search's filter — a computed
 * fact of a `SavedSearchRecord` (see `record.summary`). Structured data only: no
 * labels, currency, or locale (that is the presentation layer's job).
 */
export interface SearchSummary {
  /** Trimmed area name, or null when blank. */
  areaName: string | null;
  rental: boolean;
  /** A partial home-type selection; empty when all are selected (the default). */
  homeTypes: PropertyType[];
  /** e.g. '2+', or null when left at the '0+' default. */
  bedrooms: string | null;
  bathrooms: string | null;
  parkingSpaces: string | null;
  /** Present only when a min or max is set. */
  price: NumericRange | null;
  sqft: NumericRange | null;
  /** A non-default listing status (i.e. not 'available'), else null. */
  status: string | null;
  /** Enabled boolean amenities, in display order. */
  amenities: SearchAmenityKey[];
  locker: boolean;
  /** A maintenance-fee ceiling, or null when unset. */
  maintenanceFee: number | null;
}

const HOME_TYPE_ORDER: PropertyType[] = ['house', 'townhouse', 'condo', 'land', 'commercial', 'farm'];
const AMENITY_ORDER: SearchAmenityKey[] = ['openHouse', 'garage', 'pool', 'fireplace', 'waterfront'];
const ANY_COUNT = '0+';
const DEFAULT_STATUS = 'available';

/**
 * Derives a saved search's summary from its filter, keeping only the dimensions
 * that differ from their defaults (all home types selected, `0+` counts,
 * `available` status, etc. are omitted). Pure — emits structured facts, never
 * display strings; `ui/saved-search/format-search-summary` turns them into a
 * localized label line.
 */
export function summarizeFilter(filter: Filter): SearchSummary {
  // homeType is optional in practice: form_params is years of user-supplied
  // JSON, and a saved search written without the key would otherwise throw here
  // and take down the whole panel rather than just its own card.
  const selectedHomeTypes = HOME_TYPE_ORDER.filter(key => filter.homeType?.[key]);
  // Home types are all-enabled by default, so only a partial selection is meaningful.
  const homeTypes =
    selectedHomeTypes.length > 0 && selectedHomeTypes.length < HOME_TYPE_ORDER.length ? selectedHomeTypes : [];

  const condoOrTownhouse = filter.additional?.condoOrTownhouse;

  return {
    areaName: filter.areaName?.trim() || null,
    rental: Boolean(filter.rental),
    homeTypes,
    bedrooms: filter.bedrooms && filter.bedrooms !== ANY_COUNT ? filter.bedrooms : null,
    bathrooms: filter.bathrooms && filter.bathrooms !== ANY_COUNT ? filter.bathrooms : null,
    parkingSpaces: filter.parkingSpaces && filter.parkingSpaces !== ANY_COUNT ? filter.parkingSpaces : null,
    price: filter.priceMin || filter.priceMax ? { min: filter.priceMin ?? null, max: filter.priceMax ?? null } : null,
    sqft: filter.sqftMin || filter.sqftMax ? { min: filter.sqftMin ?? null, max: filter.sqftMax ?? null } : null,
    status: filter.status && filter.status !== DEFAULT_STATUS ? filter.status : null,
    amenities: AMENITY_ORDER.filter(key => Boolean(filter[key])),
    locker: condoOrTownhouse?.locker === 'yes',
    maintenanceFee: condoOrTownhouse?.maintenanceFee ?? null,
  };
}
